import { useEffect, useState, type ReactNode } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Home,
  LogOut,
  Menu,
  RefreshCw,
  Send,
  Settings,
  Star,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { LanguageSwitcher } from '@/components/language-switcher';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { useOwnerSession } from '@/hooks/use-owner-session';
import { isOwnerPrivatePath } from '@/lib/owner-route';

const workspaceItems = [
  { href: '/owner', label: 'Overview', icon: Home, module: 'overview' },
  { href: '/owner/remakes', label: 'Remake requests', icon: ClipboardList, module: 'remakes' },
  { href: '/owner/price-leads', label: 'Price leads', icon: Tag, module: 'price-leads' },
  { href: '/owner/affiliates', label: 'Affiliate', icon: Users, module: 'affiliates' },
  { href: '/owner/reviews', label: 'Reviews & rewards', icon: Star, module: 'reviews' },
  { href: '/owner/marketing', label: 'Marketing', icon: Send, module: 'marketing' },
  { href: '/owner/analytics', label: 'Analytics', icon: BarChart3, module: 'analytics' },
  { href: '/owner/team', label: 'Account & team', icon: Settings, module: null },
];

function moduleForOwnerPath(path: string) {
  if (path === '/owner/team') return null;
  if (path === '/owner' || path === '/owner/') return 'overview';
  if (path.startsWith('/owner/remakes')) return 'remakes';
  if (path.startsWith('/owner/price-leads')) return 'price-leads';
  if (path.startsWith('/owner/affiliates')) return 'affiliates';
  if (path.startsWith('/owner/reviews')) return 'reviews';
  if (path.startsWith('/owner/marketing')) return 'marketing';
  if (path.startsWith('/owner/analytics')) return 'analytics';
  return null;
}

function normalizePath(path: string) {
  return path.replace(/\/+$/, '') || '/';
}

export function OwnerWorkspaceRoute({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();
  const ownerSession = useOwnerSession();
  const isPrivateRoute = isOwnerPrivatePath(location);
  const requiredModule = moduleForOwnerPath(normalizePath(location));

  useEffect(() => {
    if (!ownerSession.isLoading && !ownerSession.isAuthenticated && isPrivateRoute) {
      void setLocation('/owner');
    }
  }, [isPrivateRoute, ownerSession.isAuthenticated, ownerSession.isLoading, setLocation]);

  useEffect(() => {
    if (
      !ownerSession.isLoading &&
      ownerSession.isAuthenticated &&
      requiredModule &&
      !ownerSession.canAccess(requiredModule)
    ) {
      void setLocation('/owner/team');
    }
  }, [ownerSession.isAuthenticated, ownerSession.isLoading, ownerSession.canAccess, requiredModule, setLocation]);

  if (ownerSession.isLoading) {
    return (
      <main
        role="status"
        aria-label="Checking owner access"
        className="flex min-h-[100dvh] items-center justify-center bg-muted/25 px-5"
      >
        <p className="font-mono text-[10px] uppercase tracking-[.18em] text-muted-foreground">
          Checking owner access…
        </p>
      </main>
    );
  }

  if (!ownerSession.isAuthenticated) {
    if (isPrivateRoute) {
      return (
        <main
          role="status"
          aria-label="Returning to owner sign in"
          className="flex min-h-[100dvh] items-center justify-center bg-muted/25 px-5"
        >
          <p className="font-mono text-[10px] uppercase tracking-[.18em] text-muted-foreground">
            Returning to owner sign in…
          </p>
        </main>
      );
    }
    return <>{children}</>;
  }

  return <OwnerWorkspaceFrame>{children}</OwnerWorkspaceFrame>;
}

export function OwnerWorkspaceFrame({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentPath = normalizePath(location);
  const showLabels = !collapsed || mobileOpen;
  const queryClient = useQueryClient();
  const ownerSession = useOwnerSession();
  const isFetching = useIsFetching() > 0;
  const matchesCurrentPath = ({ href }: { href: string }) => {
    const normalizedHref = normalizePath(href);
    return currentPath === normalizedHref || (
      normalizedHref !== '/owner' && currentPath.startsWith(`${normalizedHref}/`)
    );
  };
  const visibleWorkspaceItems = workspaceItems.filter(
    (item) => item.module === null || ownerSession.canAccess(item.module),
  );
  const activeItem = visibleWorkspaceItems.find(matchesCurrentPath) ?? visibleWorkspaceItems[0]!;

  const renderWorkspaceLink = ({ href, label, icon: Icon }: (typeof workspaceItems)[number]) => {
    const selected = activeItem.href === href;
    return (
      <Link
        key={href}
        href={href}
        onClick={() => setMobileOpen(false)}
        aria-label={label}
        aria-current={selected ? 'page' : undefined}
        title={showLabels ? undefined : label}
        data-testid={`link-owner-sidebar-${href.split('/').pop() || 'overview'}`}
        className={`flex min-h-11 items-center gap-3 px-3 text-xs font-semibold transition-colors ${
          showLabels ? '' : 'md:justify-center md:px-0'
        } ${
          selected
            ? 'bg-[#7b3542] text-white shadow-sm'
            : 'text-[#e3d8d0] hover:bg-white/10 hover:text-white'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {showLabels && <span>{label}</span>}
        {selected && showLabels && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#e9d3a9]" aria-hidden="true" />}
      </Link>
    );
  };

  return (
    <div className="flex min-h-[100dvh] w-full bg-muted/25 text-foreground">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close owner navigation"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      )}

      <aside
        aria-label="Owner workspace navigation"
        className={`fixed inset-y-0 left-0 z-50 flex w-[272px] shrink-0 flex-col border-r border-[#40312e] bg-[#261d1b] text-[#f8f2e9] shadow-xl transition-[width,transform] duration-200 md:sticky md:top-0 md:h-[100dvh] md:translate-x-0 md:shadow-none ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'md:w-[76px]' : 'md:w-[272px]'}`}
      >
        <div className="flex h-20 shrink-0 items-center justify-between border-b border-white/10 px-4">
          <Link
            href="/owner"
            onClick={() => setMobileOpen(false)}
            aria-label="Owner workspace overview"
            className={`flex min-w-0 items-center gap-3 ${showLabels ? '' : 'md:justify-center'}`}
          >
            <img
              src="/al-malaki-logo-white-v2.webp"
              alt=""
              aria-hidden="true"
              width={160}
              height={114}
              className="h-10 w-auto shrink-0 object-contain"
            />
            {showLabels && (
              <span className="min-w-0">
                <span className="block font-serif text-xl leading-none">al-Malaki</span>
                <span className="mt-1 block font-mono text-[8px] uppercase tracking-[.2em] text-[#cbb9ae]">Owner workspace</span>
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close owner navigation"
            className="flex h-9 w-9 shrink-0 items-center justify-center text-[#d8c9bf] hover:bg-white/10 md:hidden"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Admin sections" className="flex-1 overflow-y-auto px-3 py-5">
          {showLabels && (
            <p className="mb-3 px-3 font-mono text-[9px] uppercase tracking-[.2em] text-[#a99488]">
              Workspace
            </p>
          )}
          <div className="space-y-1">
            {visibleWorkspaceItems.map((item) => renderWorkspaceLink(item))}
          </div>
        </nav>

        <div className={`border-t border-white/10 px-4 py-4 text-[9px] leading-4 text-[#a99488] ${showLabels ? '' : 'md:px-2 md:text-center'}`}>
          {!showLabels
            ? <span className="hidden md:inline">PRIVATE</span>
            : <span>Private al-Malaki operations<br />Use Exit to end your owner session.</span>}
          <span className="sr-only">Owner navigation is available on every owner workspace page.</span>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          data-testid="button-owner-sidebar-collapse"
          className="hidden h-12 w-full shrink-0 items-center justify-center border-t border-white/10 text-[#d8c9bf] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#e9d3a9] md:flex"
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4" aria-hidden="true" />
            : <ChevronLeft className="h-4 w-4" aria-hidden="true" />}
        </button>
      </aside>

      <div className="flex min-h-[100dvh] min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border bg-background/95 px-4 backdrop-blur md:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open owner navigation"
              aria-expanded={mobileOpen}
              data-testid="button-owner-sidebar-open"
              className="flex h-9 w-9 shrink-0 items-center justify-center border border-border text-muted-foreground hover:border-primary hover:text-primary md:hidden"
            >
              <Menu className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="truncate font-mono text-[9px] uppercase tracking-[.17em] text-muted-foreground sm:text-[10px]">
              <span className="hidden sm:inline">Owner workspace / </span>
              <span className="text-foreground">{activeItem.label}</span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => void queryClient.refetchQueries({ type: 'active' })}
              disabled={isFetching}
              aria-label={isFetching ? 'Refreshing owner workspace' : 'Refresh owner workspace'}
              data-testid="button-owner-workspace-refresh"
              className="inline-flex h-9 w-9 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:cursor-wait disabled:opacity-50 sm:w-auto sm:gap-2 sm:px-3"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
              <span className="hidden text-[10px] font-bold uppercase tracking-widest sm:inline">Refresh</span>
            </button>
            {ownerSession.isAuthenticated && (
              <button
                type="button"
                onClick={() => {
                  void ownerSession.logout().then(() => setLocation('/owner'));
                }}
                disabled={ownerSession.isLoggingOut}
                aria-label="Exit owner workspace"
                data-testid="button-owner-workspace-exit"
                className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:text-primary disabled:opacity-50 sm:w-auto sm:gap-2 sm:px-3"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden text-[10px] font-bold uppercase tracking-widest sm:inline">Exit</span>
              </button>
            )}
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
        </header>
        <div className="owner-workspace-content min-w-0 flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}
